/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useContext, useEffect, useState } from 'react'
import { AuthContext } from '@/components/providers/SessionProvider/auth-context'
import MainHeader from '@/components/layout/MainHeader'
import MainFooter from '@/components/layout/MainFooter'
import Styles from './styles.module.sass'
import ScreenLoader from '@/components/loaders/ScreenLoader'
import CircleLoader from '@/components/loaders/CircleLoader'
import SocketIoContext from '@/contexts/SocketIoContext'
import ContentPaddings from '@/components/wrappers/general/ContentPaddings'
import ProgressBar from '@/components/progress/ProgressBar'
import { updateOrAddNotification } from './handlers'
import Error from '@/components/messages/Error'
import ErrorContext from '@/components/providers/ErrorProvider/ErrorContext'
import DefaultPageHeader from '@/components/layout/DefaultPageHeader'
import ItemNotFound from '@/components/messages/ItemNotFound'

const RegularTemplate = ({
  backLabel,
  backUrl,
  children,
  childrenHeader: ChildrenHeader,
  className = '',
  customTitle: CustomTitle,
  description,
  fromSamListingBatch = false,
  itemNotFound = false,
  loadingContent = false,
  loadingScreen = false,
  loadingScreenOverlay = false,
  onClickBack,
  title,
  useContentContainer = true
}) => {
  const {
    name,
    notifications,
    organization,
    updateNotifications,
    zetoniumUserId
  } = useContext(AuthContext) || {}
  const { socketIo } = useContext(SocketIoContext)
  const { error } = useContext(ErrorContext)
  const [processNotifications, setProcessNotifications] = useState([])
  const logoUrl = organization?.logoUrl || '/static-files/property-rights-logo.png'
  const isDefaultLogoUrl = !organization?.logoUrl
  const hasDefaultHeader = title || description || ChildrenHeader
  const curTitle = CustomTitle
    ? <CustomTitle />
    : hasDefaultHeader && <DefaultPageHeader
        backLabel={backLabel}
        backUrl={backUrl}
        classDefaultContent="mb-5"
        className="mb-5"
        description={description}
        onClickBack={onClickBack}
        title={title}
      >
        { ChildrenHeader && <ChildrenHeader /> }
      </DefaultPageHeader>
  const ContentContainer = useContentContainer
    ? ContentPaddings
    : ({ children }) => <>{ children }</>

  if (loadingScreenOverlay) return <ScreenLoader />

  const connectToUserRoom = (zid) => {
    if (zid) {
      socketIo.emit('joinRoom', zid)
    }
  }
  useEffect(() => {
    if (notifications) {
      setProcessNotifications(notifications)
    }
  }, [notifications])

  useEffect(() => {
    if (zetoniumUserId && socketIo) {
      connectToUserRoom(zetoniumUserId)
      socketIo.on('processNotification', (obj) => {
        if (obj) {
          const currentNotifications = updateOrAddNotification(obj, processNotifications)

          updateNotifications(currentNotifications)
          setProcessNotifications(currentNotifications)
        }
      })
    }
  }, [])

  return (
    <>
      { loadingScreen && <ScreenLoader /> }

      <MainHeader
        className="mb-md"
        logoUrl={logoUrl}
        nickname={name}
        isDefaultLogoUrl={isDefaultLogoUrl}
      />
      { processNotifications &&
        <ContentPaddings>
          <ProgressBar
            processNotifications={processNotifications}
            setProcessNotifications={setProcessNotifications}
            updateNotifications={updateNotifications}
          />
        </ContentPaddings>
      }
      <main className={`${className} ${Styles.content}`}>
        <ContentContainer>
          { loadingContent
            ? <CircleLoader />
            : error
              ? <Error error={error} />
              : <>
                  { curTitle }
                  { itemNotFound
                    ? <ItemNotFound />
                    : children
                  }
                </>
          }
        </ContentContainer>
      </main>
      <MainFooter
        logoUrl={logoUrl}
      />
    </>
  )
}

export default RegularTemplate
